import type { SkillSource } from "./index"

/**
 * Wave O1 (decision 0060): onboarding over MCP. Replaces the old `importPrompt` text and keeps every rule it had (see
 * the wave spec §2.4). The only placeholder is `{{origin}}`; no date, project or user data ever goes into the file.
 */
export const onboardProject: SkillSource = {
  name: "onboard-project",
  title: "Onboard a project into Minga",
  description:
    "Put an existing project into Minga from this repo, from notes (Notion, Linear, markdown) or from a fresh idea: find or create the Minga project, read the sources, show the person a draft, write it after their yes, check that the lead can work, and never start work. Use it when the person says onboard, import or move a project into Minga.",
  body: `# Onboard a project into Minga

You put the current state of a project into Minga: its overview, history, plans, open questions and the reasons behind
the big choices, so the project's lead can plan from it and the Overview shows where it stands. An
onboarding records history and plans. It never starts work.

"You" is the agent. "The person" is whom you work for. Every write happens after the person says yes to a draft.

## 1. Find the project

- A repo: call \`read_project\` with \`project\` = the output of \`git remote get-url origin\` and \`include: ["done"]\`
  (any remote form matches exactly). "No project" means none has this repo; \`list_projects\` shows the others.
- Found: note the overview page (\`project.overview\`: its ref, path and text), goals, KPIs, areas, items, \`you\` (the
  person's @handle) and \`history\`. Never create a duplicate: change or extend what is there. If items already exist,
  this is a second import (step 12).
- A project the person names that lacks this repo: offer \`attach_repo\` and wait for a yes.
- Several workspaces: ask which one.
- None: call \`list_runners\` for the workspace, then say which project you would create: the name, the workspace, the
  repo, the lead's name, and where the lead will think. Ask the person what to call their lead, or offer to let Minga
  pick a short name nobody in the workspace has yet. A lead's name is unique in its workspace (no other agent and no
  person may have it or its handle). Where the lead thinks is "on your runner <name> (<profile>)" when one of the
  person's runners is online now with a signed-in profile (one for your own coding tool first), else "on the
  workspace's model key" when the workspace has one, else "on any of your runners with <your coding tool>" (the lead
  waits until one is up). **Wait for the person's yes**, then call \`create_project\` (Claude Code also asks the person
  before this tool runs) with \`lead: {name}\` when they chose a name (leave it out to get the suggested one) and the
  \`idempotencyKey\` \`create-<name in lower case, words joined by hyphens>-<yyyymmddhhmm>\` (now, in UTC). Choose it
  once and resend it unchanged on a retry. A name that is taken comes back refused with a free one: ask the person
  again. Tell the person the lead's name from the answer (\`lead.name\`, \`lead.handle\`) and repeat its
  \`lead.mind\` line: it says where the lead thinks, and whose key or runner that is.
- Notes or an idea need no repo: create the project without one.
- **Then choose the session's key stem, once:** \`onboard-<project slug>-<yyyymmddhhmm>\`, with the slug from
  \`project.slug\` (read_project or create_project's answer) and the same time. Every \`idempotencyKey\` below is
  \`<stem>-<step>\`, for example \`onboard-shelf-202610111432-b\`.

## 2. Read the sources (read only)

- A repo: README, CLAUDE.md / AGENTS.md, docs/ (decision records, specs), CHANGELOG and release notes,
  \`git tag --sort=-creatordate | head -30\`, \`git log --oneline -60\`, \`git branch -a\`, and if \`gh\` works
  \`gh release list --limit 20\`, \`gh pr list --state all --limit 30\` and \`gh issue list --limit 50\`. A
  \`wrangler.jsonc\` (or similar) tells you where it runs.
- Notes: the pages, projects and documents the person names, through your own connections (Notion, Linear, files).
- Look at what shipped and when, what is half done on branches, what is planned, and why the big choices were made.
- Never write to Notion, Linear, GitHub or any other source without the person's yes.

## 3. The mapping

One rule: **Minga holds the plan and the why; the source keeps the details. Link, don't copy. No sync.**

- README, CLAUDE.md, AGENTS.md, vision docs → the overview page and the guidelines (step 4).
- Tags, CHANGELOG, store versions → done releases, oldest first. Each is a top-level \`release\` with
  \`status: "done"\`, the ship date in the title ("2.3 (Oct 1)", with the year when it is not this year) and in
  \`statusNote\` ("Shipped 2026-10-01, tag v2.3.0"), and its main features as done children (one per real feature, not
  per commit). No \`dueAt\` on them: \`dueAt\` is a plan, not a ship date. Without tags, one done release "Before Minga"
  holds what shipped.
- The version in progress (roadmap, milestone, "v2 plan") → a top-level \`release\` with \`status: "todo"\` (Next),
  \`dueAt\` if a date is known, its goals in \`goalIds\`, and a spec in \`body\` with three sections: \`## Why\` (the
  problem, and the goal it serves), \`## What\` (the requirements as a plain list), \`## Not in this release\` (scope
  cuts). Every What line gets a child item (feature, bug, chore or research) with its own spec. Unless the release is
  small enough to take in at a glance, group those items under \`workstream\` children by outcome or theme (short,
  outcome-shaped names), each item under the one it serves, so the plan reads by theme from the start.
- Later versions → releases with \`status: "backlog"\` (Later), a short spec in the same sections, \`dueAt\` if known.
- Other ongoing work that is not a version → a \`workstream\` with its children. Never put a task, bug or feature at the
  top level.
- Decision records, design docs, PR discussions → 3 to 8 decision comments on what they shaped, with a link (step 8).
  Project-wide scope cuts ("no Android") go into the overview's Non-goals instead; standing working rules ("always
  run the e2e suite before a PR") go into a guideline page.
- Open branches and draft PRs → leaves \`in_progress\`, assigned to the person, with the PR link. Only what the person
  is really doing now.
- GitHub or Linear issues → only those that belong to a release being planned, as items with the issue URL. The rest
  stay where they are. A Linear project with a target date → a release with \`dueAt\`.
- TODO / FIXME comments → nothing by default.
- Idea lists → \`idea\` items with \`status: "backlog"\`, under the release they belong to or at the top level.
- Open questions → \`question\` items under the release they affect, with that release's status (\`todo\` or
  \`backlog\`), \`assignee\` the person (\`you\` from read_project), your recommended answer in the body, and a \`blocks\`
  edge to the item each one holds. Never \`capture\` them.
- Metrics (analytics, store, revenue numbers) → KPIs (name, unit, direction, cadence, role input, lagging or health,
  source manual) and one \`record_kpi\` each, with where the number came from.
- Goals (0 to 5) → outcomes you can measure, with a target on a KPI and a deadline if one is known. A shipment is not
  a goal: it is a release.
- Slack threads, chat history → only decisions, as decision comments with a link. Never the conversation.

Every item gets a full spec in \`body\`, in your own words, with references (file paths, PR and issue URLs). Summarize
and link; never paste text from issues, PRs or comments. "Done when" checks only on \`todo\` and \`backlog\` leaves, and
only where a command or a person can verify them. Dependencies are \`blocks\` edges. Status: \`done\` for what shipped,
\`cancelled\` for what was dropped (a one-line \`statusNote\` on why), \`in_progress\` only for leaves the person works
on right now, \`todo\` for what is next, \`backlog\` for later.

## 4. The overview and the guidelines

The overview is a page: the Minga document \`project/overview.md\`. Four sections, a few short lines each, under
6,000 characters in all, in your own words:

- \`# <project name>\`, then \`## Vision\`
- \`## For whom, and their loop\`: who it is for (by name when you know it), and what they repeatedly do or
  need from it
- \`## Scope\`
- \`## Non-goals\`

How the project works goes into guideline pages under \`guidelines/\`, one topic each. \`guidelines/code.md\`
holds the build and test commands as they really run in this repo, branch and PR rules, where it runs (accounts,
services, dashboards by name), and the line "When something is missing, say exactly what, and stop." Other topics get
their own page (\`guidelines/voice.md\`, …). Workers read the guidelines that fit their task.

If the overview already has text, keep what is still true and show what changes. Never write "what needs your
go-ahead" into a page: that is the lead's mandate in Minga, not overview prose.

## 5. Areas first

A new project has no areas, and every release and workstream goes into one. Plan 1 to 3 areas as lanes (for example
App, Site, Infra) in the first write. Don't hire area agents: an area without an agent is the normal case.

## 6. Show the draft and wait

Show the person the whole draft before anything is written: the overview, the guidelines, the areas, the shipped releases, Next with its
spec, Later, the open questions with your recommended answers, the decisions, the goals and KPIs. They correct it in
the conversation. Write only after they say go.

## 7. Write it in a few calls

Use \`plan\` in calls of about 30 KB each, not one call:

- (a) areas,
- (b) shipped history, oldest first (a release's place in Implemented is the order it closes in),
- (c) Next,
- (d) Later, the open questions and the ideas.

Every call passes \`import: true\` and an \`idempotencyKey\` (\`<stem>-a\`, \`<stem>-b\`, …). Use \`$labels\` for
everything new inside a call and refs (DIC-12) from earlier answers across calls. \`dueAt\` is in epoch milliseconds.
Rationale: "Import of <project>'s current state from <the sources> on <today>. History, not a new plan."

- The overview and the guidelines are documents, not plan changes: one \`post_document\` call with the rows
  \`{path: "project/overview.md", content}\` and \`{path: "guidelines/<topic>.md", content}\`, after call (a).
  Leave out a row whose text equals what is there (it is skipped anyway). Posting the overview again makes its next
  version, with the person as its author.
- Never \`in_progress\` on a release or any other top-level item, never \`MoveItem\`, never worker or agent assignees.
  Moving a release into Now is the person's Start, in Minga.
- Report each answer's counts ("6 shipped releases and 23 done features are in"). If an answer warns that items "are
  ready", tell the person at once.
- **Keys.** A timeout or network error: resend the identical call with the same key (it is deduplicated). A refused
  call: fix it and send it with a new key (\`<stem>-b2\`); a changed body under a used key is refused. A \`Conflict\`
  that names the idempotency key: \`read_project\` to see what applied, then send only what is missing under a new key.
  Never resend a call that applied.

## 8. Decisions and KPI values

- One \`comment\` per decision with \`decision: true\`, \`target\` the ref from step 7, and one or two sentences:
  "<what>. Why: <why>. Source: <link or path>". Key: \`<stem>-d1\`, \`<stem>-d2\`, …
- One \`record_kpi\` per KPI value, with evidence (where the number came from). Key: \`<stem>-k1\`, \`<stem>-k2\`, …
- The same key rules as in step 7: a timeout → the identical call with the same key.

## 9. Check the setup

Call \`read_project\` and read its \`setup\`. If \`setup.leadCanWork\` is false, tell the person \`setup.fix\` in one
sentence. Offer the runner-setup skill (\`guide {name: "runner-setup"}\`) only when \`setup.leadOwner\` is \`"you"\` and
the fix names it; otherwise the fix says what to do (whose runner the lead needs, or what to change in Team). Nothing else
starts. Whatever happens with the setup, go on with step 10.

## 10. Ask the lead, in words only

Always call \`ask_lead\` with: "I just imported <project>'s current state: history, not a new plan. Please send me
one card: what is missing, the next step you propose, and any questions with your defaults. Whether and when work
starts stays my call." The lead reads the import through this message, so send it even when it can't work yet: it
waits for the lead. The import itself wakes no one; this message is the one hand-off.

- \`status: "answered"\`: show the person the reply and the card (decided in the Minga app).
- \`status: "working"\` (always so while the lead can't work): give the person the chat link from the answer ("<lead
  name> will answer here once its runner is up"). If you set up a runner in this session, call \`read_project\`
  again; once \`setup.leadCanWork\` is true, call \`ask_lead\` with \`replyTo\` (the answer's \`messageId\`) to
  collect the reply.
- The lead asks questions: give the person's answers back with \`ask_lead\`, or let them answer in the chat link.

Then report the created refs, anything you were unsure about, and any warnings.

## 11. Optional, only with a yes

Offer two to six lines in CLAUDE.md or AGENTS.md: "This repo is the Minga project <slug>. Capture bugs and ideas with
the minga tools." Write them only if the person says yes.

## 12. A second import

\`read_project {include: ["done"]}\` first (its \`history\` lists every closed release). Never re-send history: \`plan\`
refuses a done release that was already imported. Add only what is new. A wrong call is undone with \`undo_plan\` and
the \`changesetId\` from its answer.

## Never

- Start work, move anything into Now, or assign workers or agents.
- Write before the person said yes to the draft, or write to the sources (Notion, Linear, GitHub) without a yes.
- Paste long passages, or copy from the README, store copy, decision records, issues or PRs: summarize and link.
- Paste a secret's value, customer data or personal data into Minga text (keys go into Settings › Secrets).
- Create a second project for the same repo.
- Use more than one item per real piece of work, or titles over 80 characters.
`,
}
